(() => {
  const root = () => window.Shopify.routes.root;
  const groupItems = (cart, group) => cart.items.filter(item => item.properties?._puff_group === group);
  const identity = item => `${item.id}:${item.properties._puff_role}`;
  const quantities = items => items.reduce((result, item) => {
    const key = identity(item);
    result[key] = (result[key] || 0) + item.quantity;
    return result;
  }, {});
  const refresh = (openDrawer = false) => document.dispatchEvent(new CustomEvent('theme:cartchanged', { detail: { openDrawer } }));
  let busy = false;

  function cartIsUpdating() {
    return document.querySelector('.merge-item-refreshing, .cart-form--refreshing') ||
      [...document.querySelectorAll('cart-form .cart-item__quantity-input')].some(input => input.value !== input.dataset.initialValue);
  }

  async function request(path, body) {
    const response = await fetch(root() + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Unable to update your cart. Please try again.');
    return data;
  }

  function message(text) {
    let box = document.querySelector('.puff-cart-message');
    if (!box) {
      box = document.createElement('div');
      box.className = 'puff-cart-message';
      box.setAttribute('role', 'alert');
      document.body.append(box);
    }
    box.textContent = text;
    const link = document.createElement('a');
    link.href = root() + 'cart';
    link.textContent = 'Review cart';
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = 'Dismiss';
    close.addEventListener('click', () => box.remove());
    box.append(' ', link, ' ', close);
  }

  function setBusy(value) {
    busy = value;
    document.documentElement.toggleAttribute('data-puff-busy', value);
  }

  // Reduce only: change.js uses current line indices, including Shopify discount-split lines.
  // Re-read after every change because discount allocation can change keys and line positions.
  async function reduceTo(group, targets) {
    for (let attempt = 0; attempt < 100; attempt++) {
      const cart = await request('cart.js');
      const items = groupItems(cart, group);
      const totals = quantities(items);
      const line = cart.items.findIndex(item => item.properties?._puff_group === group && totals[identity(item)] > (targets[identity(item)] || 0));
      if (line < 0) return cart;
      const item = cart.items[line];
      const excess = totals[identity(item)] - (targets[identity(item)] || 0);
      await request('cart/change.js', { line: line + 1, quantity: Math.max(0, item.quantity - excess) });
    }
    throw new Error('Unable to finish repairing the Puff set. Please contact us before checking out.');
  }

  async function addBatch(group, items, before = {}) {
    try {
      await request('cart/add.js', { items, sections: 'cart-drawer' });
      const cart = await request('cart.js');
      const actual = quantities(groupItems(cart, group));
      const delta = quantities(items);
      const expected = { ...before };
      for (const key in delta) expected[key] = (expected[key] || 0) + delta[key];
      if (Object.keys(actual).length !== Object.keys(expected).length || Object.keys(expected).some(key => actual[key] !== expected[key])) {
        throw new Error('Not all items could be added. Please choose available accessories and try again.');
      }
    } catch (error) {
      throw new Error(`${error.message} Some items may already be in your cart. Review it and remove any incomplete Puff set before retrying.`);
    }
  }

  async function catalog() {
    const entries = await Promise.all(['tsl-laces', 'tsl'].map(async handle => {
      const response = await fetch(`${root()}collections/${handle}?section_id=puff-accessories`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Accessories could not be loaded. Please reload the choices.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = html.querySelector('[data-puff-options]');
      if (!script) throw new Error('Accessories are unavailable. Please contact us.');
      return JSON.parse(script.textContent);
    }));
    return { lace: entries[0], charm: entries[1] };
  }

  function checkStock(items, options, cart) {
    const needed = new Map();
    for (const item of items) {
      if (['puff', 'styling'].includes(item.properties._puff_role)) continue;
      const role = item.properties._puff_role;
      const variant = options[role].find(option => option.id === item.id);
      needed.set(item.id, (needed.get(item.id) || 0) + item.quantity);
      const existing = cart.items.filter(line => line.id === item.id).reduce((sum, line) => sum + line.quantity, 0);
      if (!variant?.available || !variant.tracked || needed.get(item.id) + existing > variant.stock) {
        throw new Error('There is not enough stock for these accessories, including items already in your cart. Please change your choices or quantity.');
      }
    }
  }

  class PuffBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.querySelector('[data-puff-retry]').addEventListener('click', () => this.load());
      this.querySelector('[data-puff-styling]')?.addEventListener('change', event => {
        this.querySelector('[data-puff-instructions-label]').hidden = !event.target.checked;
        this.querySelector('[data-puff-instructions]').disabled = !event.target.checked;
      });
      this.load();
    }

    async load() {
      this.ready = false;
      this.querySelector('[data-puff-retry]').hidden = true;
      this.querySelector('[data-puff-message]').textContent = 'Loading available accessories…';
      try {
        const options = await catalog();
        for (const select of this.querySelectorAll('[data-puff-choice]')) {
          const selected = select.value;
          select.replaceChildren(new Option('Choose an accessory', ''));
          for (const variant of options[select.dataset.puffChoice]) {
            const option = new Option(variant.title, variant.id);
            option.disabled = !variant.available || !variant.tracked || variant.stock < 1;
            if (option.disabled) option.textContent += ' — unavailable';
            select.append(option);
          }
          if ([...select.options].some(option => option.value === selected && !option.disabled)) select.value = selected;
          if (![...select.options].some(option => option.value && !option.disabled)) throw new Error('No accessories are currently available for this set. Please try again later.');
        }
        this.renderGrids(options);
        this.ready = true;
        this.querySelector('[data-puff-message]').textContent = '';
      } catch (error) {
        this.querySelector('[data-puff-message]').textContent = error.message;
        this.querySelector('[data-puff-retry]').hidden = false;
      }
    }

    renderGrids(options) {
      this.querySelectorAll('.puff-bundle__catalog').forEach(grid => grid.remove());
      for (const role of ['lace', 'charm']) {
        const selects = [...this.querySelectorAll(`[data-puff-choice="${role}"]`)];
        selects.forEach(select => { select.closest('label').hidden = true; select.required = false; });
        const section = document.createElement('section');
        section.className = 'puff-bundle__catalog';
        const heading = document.createElement('h3');
        const count = document.createElement('span');
        count.setAttribute('aria-live', 'polite');
        heading.append(role === 'lace' ? '1. Choose your laces ' : '2. Choose your charms ', count);
        const grid = document.createElement('div');
        grid.className = 'puff-bundle__grid';
        grid.setAttribute('role', 'group');
        grid.setAttribute('aria-label', role === 'lace' ? 'Laces — choose at least one' : 'Charms — choose at least two');
        section.append(heading, grid);
        this.querySelector('.puff-bundle__styling, [data-puff-message]').before(section);
        const sync = () => {
          const values = selects.map(select => select.value).filter(Boolean);
          count.textContent = `${values.length} selected`;
          this.querySelector('[data-puff-extras]').hidden = [...this.querySelectorAll('[data-puff-choice]')].filter(select => select.value).length <= 3;
          grid.querySelectorAll('[data-puff-card]').forEach(card => {
            const quantity = values.filter(value => value === card.dataset.puffCard).length;
            card.classList.toggle('is-selected', quantity > 0);
            const add = card.querySelector('[data-puff-pick]');
            add.disabled = card.dataset.unavailable === 'true' || quantity >= Number(card.dataset.stock);
            card.querySelector('output').textContent = quantity;
            card.querySelector('[data-puff-minus]').disabled = quantity === 0;
          });
        };
        for (const variant of options[role]) {
          const card = document.createElement('div');
          card.className = 'puff-bundle__card';
          card.dataset.puffCard = String(variant.id);
          card.dataset.stock = variant.stock;
          card.dataset.unavailable = String(!variant.available || !variant.tracked || variant.stock < 1);
          if (variant.image) {
            const image = document.createElement('img');
            image.src = variant.image;
            image.alt = '';
            image.loading = 'lazy';
            image.width = image.height = 120;
            card.append(image);
          } else {
            const placeholder = document.createElement('div');
            placeholder.className = 'puff-bundle__no-image';
            placeholder.textContent = 'No photo';
            card.append(placeholder);
          }
          const title = document.createElement('span');
          title.className = 'puff-bundle__card-title';
          title.textContent = variant.title + (card.dataset.unavailable === 'true' ? ' — unavailable' : '');
          const price = document.createElement('span');
          price.className = 'puff-bundle__card-title';
          price.textContent = `Regular price: ${variant.price}`;
          const controls = document.createElement('div');
          controls.className = 'puff-bundle__card-controls';
          const add = document.createElement('button');
          add.type = 'button';
          add.dataset.puffPick = '';
          add.textContent = '+';
          add.setAttribute('aria-label', `Add ${variant.title}`);
          add.addEventListener('click', () => {
            let select = selects.find(select => !select.value);
            if (!select) {
              const label = selects[0].closest('label').cloneNode(true);
              select = label.querySelector('select');
              select.onchange = sync;
              selects[0].closest('label').after(label);
              selects.push(select);
            }
            select.value = String(variant.id);
            sync();
          });
          {
            const minus = document.createElement('button');
            minus.type = 'button';
            minus.dataset.puffMinus = '';
            minus.textContent = '−';
            minus.setAttribute('aria-label', `Remove ${variant.title}`);
            minus.addEventListener('click', () => { const select = selects.find(select => select.value === String(variant.id)); if (select) select.value = ''; sync(); });
            const quantity = document.createElement('output');
            quantity.setAttribute('aria-label', `Selected quantity of ${variant.title}`);
            controls.append(minus, quantity);
          }
          controls.append(add);
          card.append(title, price, controls);
          grid.append(card);
        }
        selects.forEach(select => select.onchange = sync);
        sync();
      }
    }

    async add(form) {
      if (busy) return;
      const status = this.querySelector('[data-puff-message]');
      if (cartIsUpdating()) { status.textContent = 'Please wait for your previous cart change to finish.'; return; }
      const selected = [...this.querySelectorAll('[data-puff-choice]')].filter(select => select.value);
      if (!this.ready || selected.filter(select => select.dataset.puffChoice === 'lace').length < 1 || selected.filter(select => select.dataset.puffChoice === 'charm').length < 2 || !form.reportValidity()) { status.textContent = 'Choose at least one available lace and two charms before adding your Puff.'; return; }
      const data = new FormData(form);
      const quantity = Number(data.get('quantity') || 1);
      const id = Number(data.get('id'));
      if (!id || !Number.isSafeInteger(quantity) || quantity < 1) { status.textContent = 'Select a Puff size and a whole quantity of at least 1.'; return; }
      const group = crypto.randomUUID();
      const properties = {};
      for (const [key, value] of data) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      const items = [{ id, quantity, properties: { ...properties, _puff_group: group, _puff_role: 'puff', _puff_per_pair: '1' } }];
      if (data.get('selling_plan')) items[0].selling_plan = data.get('selling_plan');
      const styling = this.querySelector('[data-puff-styling]');
      if (styling?.checked) {
        const instructions = this.querySelector('[data-puff-instructions]').value.trim();
        items[0].properties._puff_styling_variant = styling.value;
        items[0].properties['Styling service'] = 'Fit my laces and charms before dispatch';
        if (instructions) items[0].properties['Styling instructions'] = instructions;
        items.push({ id: Number(styling.value), quantity, properties: {
          _puff_group: group, _puff_role: 'styling', _puff_per_pair: '1',
          ...(instructions ? { 'Styling instructions': instructions } : {})
        } });
      }
      for (const select of selected) {
        const accessoryId = Number(select.value);
        const role = select.dataset.puffChoice;
        const existing = items.find(item => item.id === accessoryId && item.properties._puff_role === role);
        if (existing) {
          existing.quantity += quantity;
          existing.properties._puff_per_pair = String(Number(existing.properties._puff_per_pair) + 1);
        } else {
          items.push({ id: accessoryId, quantity, properties: { _puff_group: group, _puff_role: role, _puff_per_pair: '1' } });
        }
      }
      setBusy(true);
      status.textContent = 'Adding your Puff set…';
      try {
        const [options, cart] = await Promise.all([catalog(), request('cart.js')]);
        checkStock(items, options, cart);
        await addBatch(group, items);
        status.textContent = 'Your Puff set is in the cart. Check the Shopify-applied offer and final total before checkout.';
        if (window.theme.settings.after_add_to_cart === 'page') location.assign(root() + 'cart');
        else refresh(true);
      } catch (error) {
        status.textContent = error.message;
        message(error.message);
        refresh(true);
      } finally {
        setBusy(false);
      }
    }
  }

  window.tslPuffBundle = {
    get busy() { return busy; },
    async adjust(item, change) {
      if (busy) return;
      if (cartIsUpdating()) { message('Please wait for your previous cart change to finish.'); return; }
      const group = item.dataset.puffGroup;
      setBusy(true);
      try {
        const cart = await request('cart.js');
        const lines = groupItems(cart, group);
        const count = lines.filter(line => line.properties._puff_role === 'puff').reduce((sum, line) => sum + line.quantity, 0);
        const quantity = change.to === 0 ? 0 : change.increase ? count + 1 : change.decrease ? count - 1 : Number(item.querySelector('.cart-item__quantity-input').value);
        const before = quantities(lines);
        const laceCount = lines.filter(line => line.properties._puff_role === 'lace').reduce((sum, line) => sum + line.quantity, 0);
        const charmCount = lines.filter(line => line.properties._puff_role === 'charm').reduce((sum, line) => sum + line.quantity, 0);
        const complete = lines.every(line => {
          const perPair = Number(line.properties._puff_per_pair);
          const stylingVariant = line.properties._puff_styling_variant;
          return Number.isSafeInteger(perPair) && perPair > 0 && before[identity(line)] === count * perPair &&
            (!stylingVariant || lines.some(service => service.properties._puff_role === 'styling' && String(service.id) === stylingVariant));
        });
        if (!Number.isSafeInteger(quantity) || quantity < 0 || (quantity > 0 && (!count || laceCount < count || charmCount < count * 2 || !complete))) {
          throw new Error('Please remove this incomplete set and choose it again on the Puff page.');
        }
        if (quantity > count) {
          const unique = [...new Map(lines.map(line => [identity(line), line])).values()];
          const items = unique.map(line => ({ id: line.id, quantity: (quantity - count) * Number(line.properties._puff_per_pair), properties: line.properties, ...(line.selling_plan_allocation ? { selling_plan: line.selling_plan_allocation.selling_plan.id } : {}) }));
          checkStock(items, await catalog(), cart);
          await addBatch(group, items, before);
        } else {
          const targets = {};
          for (const line of lines) targets[identity(line)] = quantity * Number(line.properties._puff_per_pair);
          await reduceTo(group, targets);
        }
      } catch (error) {
        message(error.message + ' Check the actual cart; if a set is incomplete, use Remove Puff set and choose it again.');
      } finally {
        setBusy(false);
        refresh();
      }
    }
  };

  // Do not let checkout race a multi-line update. Native prices still enforce eligibility.
  for (const eventName of ['click', 'submit']) document.addEventListener(eventName, event => {
    if (!busy) return;
    const target = event.target.closest('a[href*="/checkout"], [name="checkout"], .additional-checkout-buttons, form[action*="/cart"]');
    if (target) { event.preventDefault(); event.stopImmediatePropagation(); message('Please wait for the Puff cart update to finish.'); }
  }, true);
  customElements.define('puff-bundle', PuffBundle);
})();
